/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// React
import { type ReactElement } from 'react';

// Components
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/icon';

// Hooks
import { useQuantityPicker } from '@/hooks/use-quantity-picker';

// Utils
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface QuantityPickerProps {
    /** Current quantity value as string */
    value: string;
    /** Callback when quantity changes */
    onChange: (stringValue: string, numberValue: number) => void;
    /** Callback when input loses focus */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    /**
     * Minimum quantity allowed.
     *
     * Unset, the field clamps at 0 while the decrement control stops at 1 — the convention for a
     * picker beside an Add to Cart, where a zero is meaningless. Pass `min={0}` where zero IS a
     * state the user has to be able to return to, such as a per-row order quantity.
     */
    min?: number;
    /** Maximum quantity allowed (for bonus products, etc.) */
    max?: number;
    /** Product name for accessibility */
    productName?: string;
    /** Whether the picker is disabled */
    disabled?: boolean;
    /**
     * Readonly presentation: the value is still focusable and selectable and still
     * participates in form submission, but it cannot be edited and the
     * increment/decrement controls are not rendered (Figma `State=ReadOnly`).
     */
    readOnly?: boolean;
    /**
     * Control height on the system-wide size scale. `lg` is 48px and `sm` is 32px.
     * There is deliberately no `md` (40px) — see the note in the component doc block.
     */
    size?: 'lg' | 'sm';
    /** `id` for the numeric input, so an external `<label htmlFor>` can name it. */
    id?: string;
    /**
     * Accessible name for the numeric input, overriding the bare "Quantity" default.
     *
     * Needed wherever one page carries several pickers — a per-row order table, a cart line list — since
     * a screen reader otherwise announces every field identically and gives no way to tell which row it
     * is on. Note that an external `<label htmlFor={id}>` CANNOT do this job: `aria-label` outranks a
     * native label in the accessible-name computation, so the label would be ignored.
     */
    'aria-label'?: string;
    /** Additional class names for the container */
    className?: string;
}

/**
 * QuantityPicker — the SRAM **Number Input** (CRCB2B-633).
 *
 * A custom control in the shadcn idiom, with interaction logic derived from
 * Chakra UI's NumberInput (native `<input type="number">` + increment/decrement
 * buttons, ArrowUp/ArrowDown, integer-only filtering, clamp on blur). Adopted
 * rather than rebuilt: the registry has no number input.
 *
 * ## Field treatment
 * The box inherits the Text Input treatment so a quantity sits in a form row
 * without looking foreign: `--muted` fill, 1px `--input` border, value text in
 * `--input-foreground`, `hover:border-border-hover`, and focus as a
 * `--ring` border swap plus a 3px ring. Focus is applied with `focus-within`
 * because the control is a composite — the number input and both buttons are
 * separate tab stops inside one visual box.
 *
 * The resting `--border` on `--muted` is the same recorded WCAG SC 1.4.11
 * failure as the Text Input's (no adjacency reaches 3:1); built as designed and
 * escalated, not silently substituted.
 *
 * ## Size scale — read off the node, and it disagrees with the planning docs
 * Figma's Quantity Picker defines **two** steps: `Medium` = **48px** (`5845:31423`,
 * 8px padding, 8px gaps) and `Small` = **32px** (`5845:31431`, no padding, 8px
 * gaps). Both totals corroborate it — the variants measure 128×83 and 112×67
 * against a constant 35px label group.
 *
 * The change's own spec table and design.md both claim `Small` = 40px. That is
 * **wrong**; the node says 32. Under the pixel-anchored scale (`lg` 48 / `md` 40 /
 * `sm` 32) the two steps are therefore `lg` and **`sm`**, not `lg` and `md`.
 * Naming the 32px step `md` would break the one invariant the scale exists to
 * hold — that a name means the same height everywhere. Raised with the design
 * owner; do not "correct" this back to `md` without re-reading the node.
 *
 * ## Boundary and readonly states
 * `AtMin`/`AtMax` disable the decrement/increment control respectively. The
 * disabled control keeps **no fill** — it stays transparent and only its glyph
 * greys to `--disabled-foreground`, matching Figma's Icon Button Ghost disabled
 * (`1658:21756`), which declares no fill at all.
 *
 * This is a **deliberate, scoped exception to the ruling on CRCB2B-643 task 2.1n**,
 * made by the project owner on review of this component. That ruling had every ghost
 * treatment follow the *text* Button's filled disabled (`--disabled` #bcbcbc) for
 * state legibility, knowingly diverging from the Icon Button node. It was made
 * looking at buttons in isolation, and this is the context where it read worst: the
 * default quantity is 1, so the decrement control is disabled in the component's
 * *most common* state, and a solid grey block sat inside the field on first paint.
 *
 * The exception is applied at the call site (`disabled:bg-transparent`) rather than
 * by changing the `ghost` variant, so 2.1n still stands everywhere else. **Note the
 * inconsistency that leaves**: `category-pagination` uses the same icon-only ghost
 * arrows disabled at a boundary and still shows the grey block. Flagged for the
 * design owner rather than changed unasked.
 *
 * `State=ReadOnly` drops the box (no fill, no border, no inset) and omits both
 * controls entirely.
 *
 * ## Deviation from Figma — disabled keeps a fill
 * Figma's disabled Quantity Picker (`5877:27828`) declares a **white**
 * (`surface-page`) fill behind a #f5f5f5 border: on the white page that is an
 * invisible control at a 1.09:1 boundary. As with the Text Input (task 3.1m),
 * the resting `--muted` fill is kept instead, so disabled reads as a flat grey
 * block. Note the two nodes do not even agree with each other — the Text Input's
 * disabled box declares *no* fill while this one declares white. Raised with design.
 */
export default function QuantityPicker({
    value,
    onChange,
    onBlur,
    min,
    max,
    productName,
    'aria-label': ariaLabel,
    disabled = false,
    readOnly = false,
    size = 'lg',
    id,
    className,
}: QuantityPickerProps): ReactElement {
    const { t: tQuantity } = useTranslation('quantitySelector');
    const { t: tCommon } = useTranslation('common');

    const {
        inputValue,
        inputRef,
        isDecrementDisabled,
        isIncrementDisabled,
        handleIncrement,
        handleDecrement,
        handleInputChange,
        handleInputFocus,
        handleInputBlur,
        handleKeyDown,
    } = useQuantityPicker({
        value,
        onChange,
        onBlur,
        min,
        max,
    });

    return (
        <div
            data-slot="quantity-picker"
            data-size={size}
            data-disabled={disabled || undefined}
            data-readonly={readOnly || undefined}
            className={cn(
                'inline-flex items-center gap-2 rounded-ui border border-input bg-muted text-input-foreground transition-[color,box-shadow]',
                // lg 48px carries an 8px inset; sm 32px has none — the 32px icon
                // buttons fill the box edge to edge (nodes 5845:31423 / 5845:31431).
                size === 'lg' ? 'h-12 p-2' : 'h-8 p-0',
                // The composite has three tab stops inside one box, so the focus
                // treatment is hung on the box via focus-within rather than on any
                // one child. Same border swap + 3px ring as every other field.
                'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
                !disabled && !readOnly && 'hover:border-border-hover',
                // Disabled is a token recolor, not a fade (see the doc block).
                disabled && 'cursor-not-allowed border-border-disabled text-disabled-foreground',
                // Readonly drops the box entirely and keeps only the value.
                readOnly && 'border-transparent bg-transparent px-0 [--ui-shadow:0_0_#0000]',
                className
            )}>
            {/* Decrement — omitted entirely in the readonly state */}
            {!readOnly && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleDecrement}
                    disabled={disabled || isDecrementDisabled}
                    aria-label={tQuantity('decreaseQuantityForProduct', {
                        productName: productName || tCommon('product'),
                    })}
                    // No fill when disabled — the glyph greys and the box stays
                    // transparent, per Figma's Icon Button Ghost disabled
                    // (`1658:21756`). A scoped exception to task 2.1n; see the
                    // component doc above. `cn` is twMerge, so this replaces the
                    // base cva's `disabled:bg-disabled` rather than fighting it.
                    className="disabled:bg-transparent"
                    data-testid="quantity-decrement">
                    <Icon name="remove" />
                </Button>
            )}

            {/* Value */}
            <input
                ref={inputRef}
                id={id}
                type="number"
                // The DOM floor stays 0 when the caller names none, matching what the field itself
                // clamps to — the 1-step convention lives on the control, not on the input.
                min={min ?? 0}
                max={max}
                step={1}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                readOnly={readOnly}
                className={cn(
                    // 40px value column, 16px text at 3% tracking on --input-foreground,
                    // matching the Text Input's resting value color. Not bold: Figma
                    // renders it in the body weight (55 Roman), not a semibold step.
                    //
                    // Figma draws this column at 32px — 112px total = 32 + 8 + 32 + 8 + 32 — with `0`
                    // and `01` in it, which is the whole range it anticipates. A B2B order line reaches
                    // four digits, and `9999` measures ~37.5px at this size and tracking (4 digits at
                    // ~8.9px, plus 4 x 0.48px of tracking), so the designed column clipped the last one.
                    //
                    // FIXED at 40px rather than grown per value. A width that tracks the content is the
                    // same control changing size as you type, and the reflow drags the +/- buttons with
                    // it; one width that holds every value the field will realistically carry is
                    // steadier to use and cheaper to reason about. The cost is 8px: the picker is 120px
                    // where the frame says 112. Five digits would clip again — an accepted boundary,
                    // sitting well past both the >1000 confirmation and any stock ceiling.
                    'w-10 border-0 bg-transparent text-center text-base leading-[1.5] tracking-[0.03em] text-inherit outline-none',
                    'focus:ring-0 focus-visible:outline-none',
                    'disabled:cursor-not-allowed',
                    // Suppress the OS spinners — the +/- buttons are the affordance.
                    '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
                )}
                // Overridable, because "Quantity" alone is ambiguous the moment a page carries more than
                // one picker: an order table of sixteen rows announced sixteen identically named fields
                // while the two buttons either side of each one were individually named.
                //
                // It has to be THIS attribute rather than an external `<label htmlFor>`, even though the
                // `id` prop exists for exactly that: the accessible-name computation takes `aria-label`
                // ahead of a native label element, so a label supplied by a call site is silently ignored
                // while this is set. Measured, not assumed.
                //
                // Defaulted rather than required, so every caller that names nothing keeps the label it
                // has today.
                aria-label={ariaLabel ?? tQuantity('quantity')}
            />

            {/* Increment — omitted entirely in the readonly state */}
            {!readOnly && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleIncrement}
                    disabled={disabled || isIncrementDisabled}
                    aria-label={tQuantity('increaseQuantityForProduct', {
                        productName: productName || tCommon('product'),
                    })}
                    // No fill when disabled — the glyph greys and the box stays
                    // transparent, per Figma's Icon Button Ghost disabled
                    // (`1658:21756`). A scoped exception to task 2.1n; see the
                    // component doc above. `cn` is twMerge, so this replaces the
                    // base cva's `disabled:bg-disabled` rather than fighting it.
                    className="disabled:bg-transparent"
                    data-testid="quantity-increment">
                    <Icon name="add" />
                </Button>
            )}
        </div>
    );
}
